import { useEffect, useRef, useSyncExternalStore } from "react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
let snapshot = { loaded: false, broker: "dhan", enabled: true, clientId: "", accessToken: "", revision: 0 };
const listeners = new Set();
let timer, pending;

export function acceptSecretSettings(settings) {
  const broker = Object.hasOwn(settings, "broker") ? settings.broker : "dhan";
  const next = { loaded: true, broker,
    enabled: settings.credentialsEnabled !== false && Boolean(broker),
    clientId: settings.clientId || "", accessToken: settings.accessToken || "",
    revision: settings.credentialRevision || 0 };
  if (snapshot.loaded && next.revision < snapshot.revision) return;
  if (JSON.stringify(next) !== JSON.stringify(snapshot)) {
    snapshot = next;
    listeners.forEach(listener => listener());
  }
}

export async function refreshSecretCredentials() {
  if (pending) return pending;
  const token = localStorage.getItem("ct_login_token");
  const user = JSON.parse(localStorage.getItem("ct_login_user") || "null");
  if (!token || user?.role !== "admin") return;
  pending = window.fetch(`${API_BASE}/api/login/admin/secret-settings?credentialScope=secret`, {
    headers: { Authorization: `Bearer ${token}` }, cache: "no-store",
  }).then(async response => {
    const data = await response.json();
    if (response.ok && data.success) acceptSecretSettings(data.settings);
  }).catch(() => {}).finally(() => { pending = null; });
  return pending;
}

function subscribe(listener) {
  listeners.add(listener);
  if (listeners.size === 1) {
    void refreshSecretCredentials();
    timer = setInterval(refreshSecretCredentials, 5000);
  }
  return () => { listeners.delete(listener); if (!listeners.size) clearInterval(timer); };
}

export function useSecretBroker(onCredentials) {
  const current = useSyncExternalStore(subscribe, () => snapshot);
  const callback = useRef(onCredentials);
  callback.current = onCredentials;
  useEffect(() => { if (current.loaded) callback.current?.(current); }, [current]);
  return current;
}

export function secretIdLabel() { return snapshot.broker === "upstox" ? "UPSTOX USER ID" : "DHAN CLIENT ID"; }
export function secretProviderName() { return snapshot.broker === "upstox" ? "Upstox" : "Dhan"; }
export function hasSecretCredentials(clientId, accessToken) {
  return snapshot.enabled && Boolean(accessToken) && (snapshot.broker === "upstox" || Boolean(clientId));
}

// A local fetch binding is imported only by Secret Page components. Other pages,
// order requests and the browser's global fetch remain untouched.
export async function secretFetch(input, options = {}) {
  const url = new URL(typeof input === "string" ? input : input.url, window.location.origin);
  const settings = url.pathname === "/api/login/admin/secret-settings";
  const marketData = /^\/api\/(options\/|jammu\/|dhan-option-decay\/|dhan-market-logger\/|dhan-strike-market-logger\/)/.test(url.pathname);
  if (!settings && !marketData) return window.fetch(input, options);
  if (marketData && options.method?.toUpperCase() === "POST" &&
      !/\/(control|log)$/.test(url.pathname) && snapshot.loaded && !snapshot.enabled) {
    throw new Error("Secret Page credentials are OFF");
  }
  const headers = new Headers(options.headers || {});
  const token = localStorage.getItem("ct_login_token");
  if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
  if (settings) url.searchParams.set("credentialScope", "secret");
  const response = await window.fetch(url.toString(), { ...options, headers });
  if (settings && response.ok) {
    try { const data = await response.clone().json(); if (data.success) acceptSecretSettings(data.settings); }
    catch { /* The caller handles an invalid response. */ }
  }
  return response;
}
